import { EventEmitter, EVENTS } from "../../../store/EventEmitter";
import { IMediaDeliveryConfig } from "../../../types";
import { Field } from "./Field";
import "./styles/audioField.css";

export class AudioField extends Field {
    private audioElement: HTMLAudioElement | null = null;
    private mediaConfig: IMediaDeliveryConfig;
    private isCollapsed: boolean = true; // Track collapse state

    constructor(name: string, value: string, mediaConfig: IMediaDeliveryConfig, onChange: (newValue: any) => void) {
        super(name, value, onChange);
        this.mediaConfig = mediaConfig;
        this.type = 'audio '
    }

    public render(): HTMLElement {
        const container = document.createElement('div');
        container.className = 'property-field audio-field';

        // Create a label that acts as the toggle
        const label = document.createElement('label');
        // label.htmlFor = this.name;
        label.textContent = this.name.charAt(0).toUpperCase() + this.name.slice(1) + ':';
        label.className = 'accordion-label';
        label.onclick = () => this.toggleCollapse(container);

        // Add a toggle icon (e.g., ▶ or ▼) to the label
        const toggleIcon = document.createElement('span');
        toggleIcon.className = 'toggle-icon';
        toggleIcon.textContent = this.isCollapsed ? '▶' : '▼';
        label.prepend(toggleIcon);

        // Add the label to the container
        container.appendChild(label);

        // Create a wrapper for the collapsible content
        const collapsibleContent = document.createElement('div');
        collapsibleContent.className = 'collapsible-content';
        collapsibleContent.style.display = this.isCollapsed ? 'none' : 'block';

        // Create audio upload container
        const uploadContainer = document.createElement('div');
        uploadContainer.className = 'audio-upload-container';

        // Create file input
        const input = document.createElement('input');
        input.type = 'file';
        input.id = this.name;
        input.name = this.name;
        input.accept = 'audio/*';
        input.style.display = 'none';

        // Create upload button
        const uploadButton = document.createElement('button');
        uploadButton.className = 'audio-upload-button';
        uploadButton.innerHTML = '📁 Upload Audio';
        uploadButton.onclick = () => input.click();

        uploadContainer.appendChild(uploadButton);
        collapsibleContent.appendChild(uploadContainer);

        // Create audio player container
        const playerContainer = document.createElement('div');
        playerContainer.className = 'audio-player-container';

        // Create audio element
        this.audioElement = document.createElement('audio');
        this.audioElement.controls = true;

        // If there's an existing value, set it
        if (this.value) {
            this.audioElement.src = this.value;
        }

        playerContainer.appendChild(this.audioElement);
        collapsibleContent.appendChild(playerContainer);

        // Handle file upload
        input.addEventListener('change', (event) => {
            const target = event.target as HTMLInputElement;
            if (target.files && target.files.length > 0) {
                const allowedExtensions = ['mp3', 'wav', 'ogg'];
                const file = target.files[0];
                const fileExtension = file?.name.split('.').pop()?.toLowerCase();

                if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
                    EventEmitter.getInstance<any>().emit(EVENTS.VALIDATIONERROR, {
                        message: "Invalid file format. Please upload an audio file"
                    });
                    return;
                }
                // If media config then get URL from media delivery
                if (this.mediaConfig && this.mediaConfig.getURLFromFile instanceof Function) {
                    this.mediaConfig.getURLFromFile(file)
                        .then((url) => {
                            this.value = url;
                            this.audioElement!.src = url;
                            if (this.onChange) {
                                this.onChange(url);
                            }
                        })
                        .catch((error) => {
                            console.error('Error getting URL from media delivery:', error);
                        });
                } else {
                    // Else store the base64 string
                    const reader = new FileReader();
                    reader.onload = () => {
                        if (reader.result && this.audioElement) {
                            const base64String = reader.result.toString();
                            this.audioElement.src = base64String;
                            this.value = base64String;
                            // Trigger onChange to update the state
                            if (this.onChange) {
                                this.onChange(base64String);
                            }
                        }
                    };

                    reader.onerror = (error) => {
                        console.error('Error reading audio file:', error);
                    };

                    reader.readAsDataURL(file);
                }
            }
        });

        collapsibleContent.appendChild(input);
        container.appendChild(collapsibleContent);

        (container as any)['fieldObj'] = this;
        return container;
    }

    // Toggle collapse/expand state
    private toggleCollapse(container: HTMLElement): void {
        this.isCollapsed = !this.isCollapsed;
        const collapsibleContent = container.querySelector('.collapsible-content') as HTMLElement;
        const toggleIcon = container.querySelector('.toggle-icon') as HTMLSpanElement;

        if (collapsibleContent) {
            collapsibleContent.style.display = this.isCollapsed ? 'none' : 'block';
        }
        if (toggleIcon) {
            toggleIcon.textContent = this.isCollapsed ? '▶' : '▼';
        }
    }
}